import { useTranslation } from "react-i18next";
import SVG from "react-inlinesvg";
import { useNavigate } from "react-router-dom";

import { useMobile } from "@krea/customer-features/hooks";
import TimerSVG from "@krea/customer-features/images/svg/timer.svg";
import { settings } from "@krea/customer-features/settings";
import LayoutOneColumn from "@krea/customer-features/shared-components/app/one-column";
import Container from "@krea/customer-features/shared-components/container";
import NavigateAction from "@krea/customer-features/shared-components/navigate-action";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { useGetCachedLenderResponses } from "@krea/customer-features/store/lender-responses/hooks/useGetCachedLenderResponses";
import { IResolvedBid } from "@krea/customer-features/types/lenderResponse";
import { APPLICATION_NAME, BID_TYPE } from "@krea/customer-features/utils";

import SelectedBidItem from "../bid-dashboards/selected-bid";

const PostSelectBidPaidOut = () => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const navigate = useNavigate();

  const { data: { id: loanApplicationID } = {} } =
    useGetCachedCurrentLoanApplication();
  const { data: lenderResponsesData } =
    useGetCachedLenderResponses(loanApplicationID);

  const lenderBids = {
    companyLoanBids: lenderResponsesData?.companyLoanBids ?? [],
    invoicePurchaseBids: lenderResponsesData?.invoicePurchaseBids ?? [],
    realEstateBids: lenderResponsesData?.realEstateBids ?? [],
    leasingBids: lenderResponsesData?.leasingBids ?? [],
    revolvingCreditBids: lenderResponsesData?.revolvingCreditBids ?? [],
  };

  const isCustomerWeb = settings.appName === APPLICATION_NAME.CUSTOMER_WEB;
  const isPartnerWeb = settings.appName === APPLICATION_NAME.PARTNER_WEB;

  const paidOutBids: IResolvedBid[] = [];
  const paidOutBidTypes: BID_TYPE[] = [];
  if (!lenderResponsesData) return null;

  for (const [key, bidArray] of Object.entries(lenderBids)) {
    for (const bid of bidArray) {
      if (bid.lenderResponse.paidOutAt) {
        paidOutBids.push(bid);

        switch (key) {
          case "companyLoanBids":
            paidOutBidTypes.push(BID_TYPE.COMPANY_LOAN);
            break;
          case "invoicePurchaseBids":
            paidOutBidTypes.push(BID_TYPE.FACTORING);
            break;
          case "realEstateBids":
            paidOutBidTypes.push(BID_TYPE.REAL_ESTATE);
            break;
          case "leasingBids":
            paidOutBidTypes.push(BID_TYPE.LEASING);
            break;
          case "revolvingCreditBids":
            paidOutBidTypes.push(BID_TYPE.REVOLVING_CREDIT);
            break;
        }
      }
    }
  }

  return (
    <LayoutOneColumn wide>
      <Container>
        <div className="tw-mb-4 lg:tw-flex lg:tw-justify-between">
          {isMobile ? (
            <h3>{t("dash.headingPaidOutOffers")}</h3>
          ) : (
            <h1>{t("dash.headingPaidOutOffers")}</h1>
          )}

          <div className="tw-mb-3 tw-inline-block lg:tw-mb-0">
            <div className="tw-flex tw-rounded-full tw-border tw-border-solid tw-border-gray-200 tw-py-2 tw-pl-1 tw-pr-2">
              <SVG
                className="tw-h-[18px] md:tw-mr-px md:tw-mt-px md:tw-h-[20px]"
                src={TimerSVG}
              />
              <p className="tw-m-0 tw-text-xs md:tw-text-sm">
                {t("dash.biddingClosed")}
              </p>
            </div>
          </div>
        </div>

        {paidOutBids.map((bid, index) => (
          <SelectedBidItem
            key={bid.id}
            onSelectLenderResponseClick={() => {}}
            bid={bid}
            bidType={paidOutBidTypes[index]}
            isPaidOutPage={true}
          />
        ))}

        <NavigateAction
          className={"tw-mt-4"}
          title={t("postSelect.actions.goToDashboard.title")}
          button={{
            text: t("postSelect.actions.goToDashboard.buttonText"),
            onClick: () => {
              if (isCustomerWeb) {
                navigate("/dashboard");
              } else if (isPartnerWeb) {
                navigate(`/loan-applications/${loanApplicationID}/dashboard`);
              }
            },
          }}
        />

        {isCustomerWeb ? (
          <NavigateAction
            className={"tw-mt-4"}
            title={t("postSelect.actions.createNewApplication.title")}
            button={{
              text: t("postSelect.actions.createNewApplication.buttonText"),
              onClick: () => {
                navigate("/application");
              },
            }}
          />
        ) : null}
      </Container>
    </LayoutOneColumn>
  );
};

export default PostSelectBidPaidOut;
